// Thin client for lattice-service (port 8016): objectives, job submission,
// job polling, STL download URL. Mirrors the authedFetch call shape used by
// the analyze/dft fetches in AppShell.jsx.
import { doc, getDoc } from 'firebase/firestore';
import { authedFetch } from './authedFetch';
import { firestoreDb } from '../config/firebase';
import { capabilityDeniedMessage } from '../utils/capabilityErrors';

async function _json(resPromise) {
  const res = await resPromise;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const denied = capabilityDeniedMessage(data);
    const detail = typeof data.detail === 'string' ? data.detail : (data.detail ? JSON.stringify(data.detail) : '');
    throw new Error(denied || detail || `HTTP ${res.status}`);
  }
  return data;
}

// --- Objectives cache -------------------------------------------------------
//
// The objectives registry is a SERVICE-PUBLISHED projection: lattice-service
// writes the full registry (models/lattice-service/lattice/objectives.py, the
// single source of truth) to the Firestore doc latticeObjectives/registry on
// every boot (see main.py's startup hook). Reading that doc is now the
// wizard's primary path -- it is always warm, so opening the wizard no longer
// has to wake a scale-to-zero lattice-service instance just to read metadata.
// The service itself still only boots for an actual run (POST /lattice/jobs).
//
// Source order, closest/cheapest first:
//   1. memo/localStorage cache (below) -- a fresh hit resolves instantly, no
//      Firestore read or network call at all.
//   2. Firestore doc latticeObjectives/registry -- the primary source once
//      the cache misses or goes stale. Validated for shape (a non-empty
//      array of objectives, each with id/label/scale/status) before being
//      trusted; a missing or malformed doc, or a read that doesn't resolve
//      within FIRESTORE_READ_TIMEOUT_MS, is treated as a miss, not an error.
//   3. GET {baseUrl}/lattice/objectives via authedFetch -- last resort, for
//      local dev without Firebase configured, or a missing/malformed/slow doc.
//      This is the only path that can wake a cold lattice-service instance.
//
// Whichever source actually answers is what gets cached (memo + localStorage,
// same TTL either way): a stale hit still resolves instantly (stale-while-
// revalidate) but kicks off a background refresh -- through the same source
// order -- so the next call is fresh.

const OBJECTIVES_TTL_MS = 60 * 60 * 1000; // 1 hour

const _memo = new Map(); // baseUrl -> { objectives, fetchedAt }
const _inflight = new Map(); // baseUrl -> Promise<{ objectives }>

function _storageKey(baseUrl) {
  return `lattice_objectives_cache::${baseUrl}`;
}

function _isValidEntry(entry) {
  return !!entry && typeof entry === 'object'
    && Array.isArray(entry.objectives)
    && typeof entry.fetchedAt === 'number';
}

function _isFresh(entry) {
  return _isValidEntry(entry) && (Date.now() - entry.fetchedAt) < OBJECTIVES_TTL_MS;
}

// Defensive JSON handling: localStorage can hold a corrupted, truncated, or
// shape-mismatched entry (a prior schema, manual tampering, a partial write
// interrupted mid-JSON.stringify), and reading it must never throw or return
// something fetchObjectives' callers would choke on.
function _readLocalStorageEntry(baseUrl) {
  try {
    const raw = window.localStorage?.getItem(_storageKey(baseUrl));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return _isValidEntry(parsed) ? parsed : null;
  } catch (_) {
    return null;
  }
}

function _writeLocalStorageEntry(baseUrl, entry) {
  try {
    window.localStorage?.setItem(_storageKey(baseUrl), JSON.stringify(entry));
  } catch (_) {
    // Quota exceeded, privacy mode, localStorage unavailable, etc: the
    // in-memory memo still works for the rest of this tab's life, so this
    // is a soft failure, not a reason to break the caller's fetch.
  }
}

function _readCachedEntry(baseUrl) {
  const mem = _memo.get(baseUrl);
  if (mem) return mem;
  const stored = _readLocalStorageEntry(baseUrl);
  if (stored) _memo.set(baseUrl, stored); // seed the memo from localStorage
  return stored;
}

// True only for a doc shape the wizard can actually use: a non-empty array
// where every entry carries the fields LatticeWizard.jsx reads -- id/label/
// scale (ScaleStep/ObjectiveStep) and status (live vs coming_soon filtering;
// ObjectiveStep only lists status === 'live' entries, and a doc missing
// status on every entry would otherwise validate, cache, and render an
// empty objective picker with no fallback, since every entry would just get
// filtered out silently). Anything else -- an empty array, a missing field,
// a non-array -- is treated as a miss so the caller falls through to the
// service instead of handing the wizard something it can't render.
function _isValidRegistryDoc(objectives) {
  return Array.isArray(objectives) && objectives.length > 0 && objectives.every((o) => (
    o && typeof o === 'object'
    && typeof o.id === 'string' && o.id
    && typeof o.label === 'string' && o.label
    && typeof o.scale === 'string' && o.scale
    && typeof o.status === 'string' && o.status
  ));
}

// The Firestore read should resolve in milliseconds (a single small doc);
// anything slower than this means Firestore itself is degraded, and the
// service fallback is the better bet rather than leaving a cold wizard open
// stalled on it. 4s keeps a degraded Firestore connection from stretching
// the wizard's cold open anywhere near the old 120s-bounded (job-poll-scale)
// path -- resolving null on timeout, same as any other miss, so
// _refreshObjectives just falls through to the service.
const FIRESTORE_READ_TIMEOUT_MS = 4000;

function _timeoutAfter(ms) {
  return new Promise((resolve) => { setTimeout(() => resolve(null), ms); });
}

// Read the service-published mirror. Never throws and never outlives
// FIRESTORE_READ_TIMEOUT_MS: a missing doc, a malformed doc, firestoreDb
// being null (Firebase not configured, e.g. local dev without a .env), a
// slow/degraded Firestore connection, or Firestore being unreachable all
// resolve to null so the caller falls back to the service instead of
// failing (or stalling) the whole fetch. Racing the timeout only decides
// which result this call uses -- the underlying getDoc() is not cancelled
// and any late resolution is simply ignored.
async function _readFirestoreRegistry() {
  if (!firestoreDb) return null;
  try {
    const snap = await Promise.race([
      getDoc(doc(firestoreDb, 'latticeObjectives', 'registry')),
      _timeoutAfter(FIRESTORE_READ_TIMEOUT_MS),
    ]);
    if (!snap) return null; // timed out
    if (!snap.exists()) return null;
    const objectives = snap.data()?.objectives;
    return _isValidRegistryDoc(objectives) ? objectives : null;
  } catch (_) {
    return null;
  }
}

// Actually resolves the objectives, in source order: Firestore mirror first,
// the service itself only if that misses. Deduplicates concurrent callers
// for the same baseUrl (e.g. a wizard open landing at nearly the same moment
// a background TTL refresh does) so a cold instance only ever gets woken by
// one in-flight request.
function _refreshObjectives(baseUrl) {
  if (_inflight.has(baseUrl)) return _inflight.get(baseUrl);
  const p = (async () => {
    const fromFirestore = await _readFirestoreRegistry();
    let objectives = fromFirestore;
    if (!objectives) {
      const data = await _json(authedFetch(`${baseUrl}/lattice/objectives`));
      objectives = Array.isArray(data?.objectives) ? data.objectives : [];
    }
    const entry = { objectives, fetchedAt: Date.now() };
    _memo.set(baseUrl, entry);
    _writeLocalStorageEntry(baseUrl, entry);
    return { objectives };
  })()
    .finally(() => { _inflight.delete(baseUrl); });
  _inflight.set(baseUrl, p);
  return p;
}

export function fetchObjectives(baseUrl) {
  const cached = _readCachedEntry(baseUrl);
  if (_isFresh(cached)) {
    return Promise.resolve({ objectives: cached.objectives });
  }
  if (cached) {
    // Stale-while-revalidate: serve what we have immediately, refresh in the
    // background. Swallow the refresh's own rejection here -- a failed
    // background refresh must not turn into an unhandled rejection; the
    // caller already got its (stale but usable) answer.
    _refreshObjectives(baseUrl).catch(() => {});
    return Promise.resolve({ objectives: cached.objectives });
  }
  return _refreshObjectives(baseUrl);
}

export function submitLatticeJob(baseUrl, body) {
  return _json(authedFetch(`${baseUrl}/lattice/jobs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }));
}

export function pollLatticeJob(baseUrl, jobId) {
  return _json(authedFetch(`${baseUrl}/lattice/jobs/${jobId}`));
}

export function latticeStlUrl(baseUrl, jobId) {
  return `${baseUrl}/lattice/jobs/${jobId}/stl`;
}
